import {httpInterceptor} from "./interceptor";
import {toast} from "react-toastify";
import {ATTENDANCE_WINDOW_CLOSED, ATTENDANCE_CLOSED_MESSAGE} from '../utils/digitalAttendance';

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_MEETING_REQUEST_FILES_SIZE_BYTES = 5 * 1024 * 1024;

function withNoCache(resource) {
    const cacheBreaker = `_t=${Date.now()}`;
    return resource.includes('?')
        ? `${resource}&${cacheBreaker}`
        : `${resource}?${cacheBreaker}`;
}

function appendIfDefined(formData, key, value) {
    if (value === undefined || value === null || value === '') return;
    formData.append(key, value);
}

function resolveImageSlot(image, index, maxSlots = 3) {
    const requestedBySlot = Number(image?.slot);
    const requestedById = Number(image?.id);
    const requested = Number.isInteger(requestedBySlot)
        ? requestedBySlot
        : Number.isInteger(requestedById)
            ? requestedById + 1
            : index + 1;
    if (!Number.isInteger(requested)) return null;
    if (requested < 1 || requested > maxSlots) return null;
    return requested;
}

function validateMaxSize(file) {
    const size = Number(file?.size ?? 0);
    if (size > MAX_UPLOAD_SIZE_BYTES) {
        throw new Error('MAX_FILE_SIZE_10MB');
    }
}

function buildEvidenceFormData(payload, maxSlots = 3) {
    const formData = new FormData();
    appendIfDefined(formData, 'schoolId', payload?.schoolId);
    appendIfDefined(formData, 'groupId', payload?.groupId);
    appendIfDefined(formData, 'meetingGroupId', payload?.meetingGroupId);
    appendIfDefined(formData, 'meetingDate', payload?.meetingDate);
    appendIfDefined(formData, 'goal', payload?.goal);
    appendIfDefined(formData, 'notes', payload?.notes);
    appendIfDefined(formData, 'userId', payload?.userId);
    appendIfDefined(formData, 'socializationNumber', payload?.socializationNumber);

    (payload?.images || []).forEach((image, index) => {
        const file = image?.file;
        if (!file) return;
        validateMaxSize(file);
        const slot = resolveImageSlot(image, index, maxSlots);
        if (!slot) return;
        formData.append(`slot_${slot}`, file, file.name || `slot_${slot}`);
    });

    return formData;
}

export async function getNeighborhoods(municipality) {
    try {
        if (municipality) {
            const response = await httpInterceptor.get(`neighborhoods/list?municipality=${municipality}`);
            if (response?.status === 200) {
                return response.data.data;
            } else if (response && response.success) {
                return response.data
            }
        }
    } catch (error) {
        toast('Error obteniendo barrios intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getResourceTypes() {
    try {
        const response = await httpInterceptor.get('resource-types/list');
        if (response?.status === 200) {
            return response.data.data;
        } else if (response && response.success) {
            return response.data
        }
    } catch (error) {
        toast('Error obteniendo data del servidor intente de nuevo', {
            type: 'error'
        });
    }
}

export async function getUsers({ noCache = true } = {}) {
    try {
        const cacheSuffix = noCache ? `?_t=${Date.now()}` : '';
        const response = await httpInterceptor.get(`users/list${cacheSuffix}`, {
            headers: noCache ? {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
                Expires: '0'
            } : undefined
        });
        if (response?.status === 200) {
            return response.data.data;
        } else if (response && response.success) {
            return response.data
        }
    } catch (error) {
        toast('Error obteniendo usuarios del servidor intente de nuevo', {
            type: 'error'
        });
    }
}

export async function logoutUser() {
    try {
        const response = await httpInterceptor.post('users/logout');
        if (response?.status === 200) {
            return true;
        } else if (response && response.success) {
            return true;
        }
    } catch (error) {
        return false;
    }
    return false;
}

export async function getLogisticsRequestContext() {
    const response = await httpInterceptor.get('logistics-requests/context');
    return response?.data || null;
}

export async function getLogisticsBagContext() {
    const response = await httpInterceptor.get(withNoCache('logistics-bags/context'));
    return response?.data || { mediators: [], bags: [] };
}

export async function createLogisticsBag(payload) {
    const response = await httpInterceptor.post('logistics-bags', payload);
    return response?.data || null;
}

export async function getLogisticsRequests() {
    const response = await httpInterceptor.get(withNoCache('logistics-requests'));
    return Array.isArray(response?.data) ? response.data : [];
}

export async function createLogisticsRequest(payload) {
    const response = await httpInterceptor.post('logistics-requests', payload);
    return response?.data || null;
}

export async function updateLogisticsRequest(requestId, payload) {
    const response = await httpInterceptor.put(`logistics-requests/${requestId}`, payload);
    return response?.data || null;
}

export async function getLogisticsRequestHistory(requestId) {
    const response = await httpInterceptor.get(
        withNoCache(`logistics-requests/${requestId}/history`)
    );
    return Array.isArray(response?.data) ? response.data : [];
}

export async function getLogisticsEvidenceContext(requestId) {
    const response = await httpInterceptor.get(
        withNoCache(`logistics-requests/${requestId}/evidence-context`)
    );
    return response?.data || null;
}

export async function downloadLogisticsEvidenceArchive(requestId, payload) {
    return await httpInterceptor.post(
        `logistics-requests/${requestId}/evidence-archive`,
        payload,
        { responseType: 'blob' }
    );
}

export async function reviewLogisticsRequest(requestId, payload) {
    const formData = new FormData();
    formData.append('action', payload.action || '');
    formData.append('code', payload.code || '');
    formData.append('observation', payload.observation || '');
    if (payload.approvalPdf) formData.append('approvalPdf', payload.approvalPdf);
    const response = await httpInterceptor.patch(
        `logistics-requests/${requestId}/review`,
        formData
    );
    return response?.data || null;
}

export async function getMunicipalities(zoneId) {
    try {
        const resource = zoneId ? `municipalities/list-zone?zone=${zoneId}` : 'municipalities/list';
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data
        }
        return data

    } catch (error) {
        toast('Error obteniendo data del servidor intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getZones() {
    try {
        const response = await httpInterceptor.get(`/zones/list`);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo datos intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getSchools(userId) {
    try {
        const resource = userId ? `schools/list?facilitator=${userId}` : 'schools/list';
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo Proceso Formativo intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function createControlSocialActivity(payload) {
    return await httpInterceptor.post('control-social-activities', payload);
}

export async function getControlSocialActivities() {
    const response = await httpInterceptor.get('control-social-activities');
    return Array.isArray(response?.data) ? response.data : [];
}

export async function createControlSocialActivityGroup(activityId, payload) {
    try {
        const response = await httpInterceptor.post(
            `control-social-activities/${activityId}/groups`,
            payload
        );

        return {
            success: Boolean(response?.success),
            data: response?.data ?? null
        };
    } catch (error) {
        return {
            success: false,
            code: error?.response?.data?.message
        };
    }
}

export async function getControlSocialActivityGroup(activityId, groupId) {
    try {
        const response = await httpInterceptor.get(
            `control-social-activities/${activityId}/groups/${groupId}`
        );
        return response?.success ? response.data : null;
    } catch (error) {
        toast('No fue posible cargar el grupo de Control Social', {
            type: 'error'
        });
    }
    return null;
}

export async function deleteControlSocialActivity(activityId) {
    try {
        const response = await httpInterceptor.delete(
            `control-social-activities/${activityId}`
        );
        return Boolean(response?.success);
    } catch (error) {
        toast('No fue posible eliminar la actividad de Control Social', {
            type: 'error'
        });
    }
    return false;
}

export async function deleteControlSocialActivityGroup(groupId) {
    try {
        const response = await httpInterceptor.delete(
            `control-social-activities/groups/${groupId}`
        );
        return Boolean(response?.success);
    } catch (error) {
        toast('No fue posible eliminar el grupo de Control Social', {
            type: 'error'
        });
    }
    return false;
}

export async function updateControlSocialGroupProjectLink(groupId, description) {
    try {
        const response = await httpInterceptor.put(
            `control-social-activities/groups/${groupId}/project-link`,
            {description}
        );
        return response?.success ? response.data : response?.data?.data ?? null;
    } catch (error) {
        toast('Error guardando el link del proyecto', {type: 'error'});
    }
    return null;
}

export async function finishControlSocialGroup(groupId) {
    try {
        const response = await httpInterceptor.put(
            `control-social-activities/groups/${groupId}/status`,
            {status: 'FINALIZADO'}
        );
        return Boolean(response?.success || response?.status === 200);
    } catch (error) {
        toast('Error finalizando el grupo', {type: 'error'});
    }
    return false;
}

export async function getTerritoryVisitContext() {
    try {
        const response = await httpInterceptor.get('territory-visits/context');
        return response?.data || null;
    } catch (error) {
        toast('No fue posible cargar las opciones del formulario de visitas', {
            type: 'error'
        });
    }
    return null;
}

export async function createTerritoryVisit(payload) {
    try {
        const formData = new FormData();
        Object.entries(payload || {}).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') return;
            if (key === 'photo') {
                formData.append(key, value);
                return;
            }
            formData.append(
                key,
                typeof value === 'object' ? JSON.stringify(value) : value
            );
        });
        const response = await httpInterceptor.post('territory-visits', formData);
        return response?.data || true;
    } catch (error) {
        const responseData = error?.response?.data || {};
        const code = String(responseData.code || responseData.message || '');
        const messages = {
            ADMIN_REQUIRED: 'La sesión no corresponde a un usuario administrador',
            SESSION_TYPE_INVALID: 'Seleccione un tipo de sesión válido',
            RELATED_FIELDS_REQUIRED: 'Falta seleccionar el grupo, la institución, la comuna o el mediador',
            SESSION_DATE_INVALID: 'Seleccione una fecha de realización válida',
            GROUP_SCHOOL_MISMATCH: 'El grupo seleccionado no pertenece a la entidad aliada',
            GROUP_MEDIATOR_MISMATCH: 'El mediador asociado al grupo cambió. Seleccione nuevamente el código del grupo',
            SESSION_TIME_INVALID: 'La hora final debe ser posterior a la hora de inicio',
            MEDIATOR_INVALID: 'El mediador seleccionado no es válido o está inactivo',
            OBSERVER_POSITION_INVALID: 'Ingrese un cargo del observador válido',
            METHODOLOGY_ANSWERS_REQUIRED: 'Responda Sí o No en toda la observación metodológica',
            FACILITATOR_ANSWERS_REQUIRED: 'Responda Sí o No en todas las capacidades del facilitador',
            PARTICIPANT_ANSWERS_REQUIRED: 'Responda Sí o No en todas las capacidades del grupo participante',
            PROBLEM_AXES_REQUIRED: 'Seleccione al menos un eje problematizador válido',
            PHOTO_INVALID: 'La fotografía debe ser JPG, PNG o WEBP y pesar máximo 5 MB',
            RELATED_RECORD_NOT_FOUND: 'Uno de los registros seleccionados ya no existe',
            TERRITORY_VISIT_SAVE_FAILED: 'No fue posible guardar la visita. Verifique los datos e inténtelo nuevamente',
            '23503': 'Uno de los registros seleccionados ya no está disponible'
        };
        toast(messages[code] || 'No fue posible registrar la visita', {
            type: 'error'
        });
    }
    return false;
}

export async function getTerritoryVisits() {
    try {
        const response = await httpInterceptor.get('territory-visits');
        return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
        toast('No fue posible cargar las visitas registradas', {
            type: 'error'
        });
    }
    return [];
}

export async function getCalendarEvents(filters = {}) {
    try {
        const searchParams = new URLSearchParams();
        Object.entries(filters || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                searchParams.set(key, value);
            }
        });

        const query = searchParams.toString();
        const response = await httpInterceptor.get(
            query ? `calendar-events?${query}` : 'calendar-events'
        );
        let data = [];
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return Array.isArray(data) ? data : [];
    } catch (error) {
        toast('Error obteniendo eventos del calendario', {
            type: 'error'
        });
    }
    return [];
}

export async function getIncidenceActionContext() {
    try {
        const response = await httpInterceptor.get('incidence-actions/context');
        return response?.data || {
            mediators: [], groups: [], municipalities: [], neighborhoods: []
        };
    } catch (error) {
        toast('No fue posible cargar la información para la acción de incidencia', {
            type: 'error'
        });
        return { mediators: [], groups: [], municipalities: [], neighborhoods: [] };
    }
}

export async function getIncidenceActionGroupParticipants(groupIds) {
    try {
        const response = await httpInterceptor.post('incidence-actions/groups/participants', {
            groupIds: Array.isArray(groupIds) ? groupIds : []
        });
        return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
        toast('No fue posible cargar los participantes de los grupos', { type: 'error' });
        return null;
    }
}

export async function getIncidenceActions() {
    try {
        const response = await httpInterceptor.get('incidence-actions');
        return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
        toast('No fue posible cargar las acciones de incidencia', { type: 'error' });
        return [];
    }
}

export async function getIncidenceAction(actionId) {
    try {
        const response = await httpInterceptor.get(`incidence-actions/${actionId}`);
        return response?.data || null;
    } catch (error) {
        toast('No fue posible cargar la acción de incidencia', { type: 'error' });
        return null;
    }
}

export async function downloadIncidenceActionAttendance(actionId, digitalAttendance = null) {
    const formData = new FormData();
    if (digitalAttendance) {
        formData.append('digitalAttendance', digitalAttendance, `FO-GINF-093_${actionId}.pdf`);
    }
    return await httpInterceptor.post(
        `incidence-actions/${actionId}/attendance`,
        formData,
        { responseType: 'blob' }
    );
}

export async function searchIncidenceActionBeneficiary(document) {
    try {
        const params = new URLSearchParams({ document: String(document || '').trim() });
        const response = await httpInterceptor.get(
            `incidence-actions/beneficiaries/search?${params.toString()}`
        );
        return response?.data || null;
    } catch (error) {
        const code = String(error?.response?.data?.message || '').trim();
        toast(
            code === 'DOCUMENT_REQUIRED'
                ? 'Ingresa un número de documento'
                : 'No fue posible buscar el participante',
            { type: 'warning' }
        );
        return null;
    }
}

function buildIncidenceActionFormData(payload) {
        const formData = new FormData();
        formData.append('name', payload.name);
        formData.append('mediatorIds', JSON.stringify(payload.mediatorIds || []));
        formData.append('groupIds', JSON.stringify(payload.groupIds || []));
        formData.append('actionDate', payload.actionDate);
        formData.append('startTime', payload.startTime);
        formData.append('endTime', payload.endTime);
        formData.append('topic', payload.topic);
        formData.append('municipality', payload.municipality);
        formData.append('neighborhood', payload.neighborhood);
        formData.append('place', payload.place);
        formData.append('address', payload.address);
        for (const field of ['logbook', 'methodology', 'attendance']) {
            const file = payload[field];
            if (!file) continue;
            validateMaxSize(file);
            formData.append(field, file, file.name);
        }

        return formData;
}

function handleIncidenceActionSaveError(error) {
        const code = String(error?.response?.data?.message || error?.message || '').trim();
        const messages = {
            MAX_FILE_SIZE_10MB: 'Cada archivo debe pesar máximo 10 MB',
            INCIDENCE_ACTION_NAME_REQUIRED: 'Ingresa el nombre de la acción de incidencia',
            INCIDENCE_ACTION_MEDIATORS_REQUIRED: 'Selecciona al menos un mediador',
            INCIDENCE_ACTION_MEDIATORS_INVALID: 'Uno de los mediadores seleccionados ya no está disponible',
            INCIDENCE_ACTION_GROUPS_REQUIRED: 'Selecciona al menos un grupo',
            INCIDENCE_ACTION_GROUPS_INVALID: 'Revisa que los grupos pertenezcan a los mediadores seleccionados',
            INCIDENCE_ACTION_SIGNATURE_INVALID: 'Una de las firmas no es válida. Vuelve a registrarla',
            INCIDENCE_ACTION_REQUIRED_EVIDENCES_MISSING: 'Al finalizar la asistencia debes cargar la bitácora y la ficha metodológica',
            INCIDENCE_ACTION_DATE_REQUIRED: 'Selecciona la fecha de la acción',
            INCIDENCE_ACTION_TIME_INVALID: 'La hora final debe ser posterior a la hora de inicio',
            INCIDENCE_ACTION_TOPIC_REQUIRED: 'Ingresa el tema de la acción',
            INCIDENCE_ACTION_MUNICIPALITY_REQUIRED: 'Selecciona la comuna',
            INCIDENCE_ACTION_NEIGHBORHOOD_REQUIRED: 'Selecciona el barrio',
            INCIDENCE_ACTION_PLACE_REQUIRED: 'Ingresa el lugar de la acción',
            INCIDENCE_ACTION_ADDRESS_REQUIRED: 'Ingresa la dirección de la acción',
            INCIDENCE_ACTION_EVIDENCE_INVALID: 'Revisa el formato o tamaño de los archivos',
            INCIDENCE_ACTION_BENEFICIARY_NOT_FOUND: 'Uno de los participantes ya no está disponible'
        };
        toast(messages[code] || 'No fue posible guardar la acción de incidencia', {
            type: code in messages ? 'warning' : 'error'
        });
        return { success: false, code };
}

function handleIncidenceSignatureError(error, fallback) {
    const code = String(error?.response?.data?.message || '').trim();
    const messages = {
        INCIDENCE_ACTION_ATTENDANCE_CLOSED: 'El periodo de asistencia ya finalizó',
        INCIDENCE_ACTION_SIGNATURE_INVALID: 'La firma no es válida. Intenta registrarla nuevamente',
        INCIDENCE_ACTION_BENEFICIARY_NOT_FOUND: 'La persona ya no pertenece a los grupos seleccionados'
    };
    toast(messages[code] || fallback, { type: code in messages ? 'warning' : 'error' });
    return { success: false, code };
}

export async function saveIncidenceActionSignature(actionId, beneficiaryId, signature) {
    try {
        const response = await httpInterceptor.post(
            `incidence-actions/${actionId}/participants/${beneficiaryId}/signature`,
            { signature }
        );
        return { success: Boolean(response?.success), data: response?.data || null };
    } catch (error) {
        return handleIncidenceSignatureError(error, 'No fue posible guardar la firma');
    }
}

export async function removeIncidenceActionSignature(actionId, beneficiaryId) {
    try {
        const response = await httpInterceptor.delete(
            `incidence-actions/${actionId}/participants/${beneficiaryId}/signature`
        );
        return { success: Boolean(response?.success), data: response?.data || null };
    } catch (error) {
        return handleIncidenceSignatureError(error, 'No fue posible retirar la firma');
    }
}

export async function createIncidenceAction(payload) {
    try {
        const response = await httpInterceptor.post(
            'incidence-actions',
            buildIncidenceActionFormData(payload)
        );
        return { success: Boolean(response?.success), data: response?.data || null };
    } catch (error) {
        return handleIncidenceActionSaveError(error);
    }
}

export async function updateIncidenceAction(actionId, payload) {
    try {
        const response = await httpInterceptor.put(
            `incidence-actions/${actionId}`,
            buildIncidenceActionFormData(payload)
        );
        return { success: Boolean(response?.success), data: response?.data || null };
    } catch (error) {
        return handleIncidenceActionSaveError(error);
    }
}

export async function createCalendarEvent(payload) {
    try {
        const response = await httpInterceptor.post('calendar-events', payload);
        if (response?.status === 200 || response?.status === 201) {
            return {
                success: true,
                data: response?.data?.data ?? null
            };
        } else if (response && response.success) {
            return {
                success: true,
                data: response.data ?? null
            };
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();

        if (errorCode === 'SCHOOL_NOT_ALLOWED') {
            toast('No puedes crear eventos para esta Entidad Aliada', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'STATUS_INVALID') {
            toast('Selecciona un estado valido', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENCOUNTER_TYPE_REQUIRED') {
            toast('Selecciona el tipo de encuentro', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENCOUNTER_TYPE_TOO_LONG') {
            toast('El tipo de encuentro es demasiado largo', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENCOUNTER_GROUPS_NOT_CREATED') {
            toast('Primero debes crear el grupo al que vas a asistir', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENCOUNTER_GROUP_REQUIRED') {
            toast('Selecciona el grupo al que asistirás', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENCOUNTER_SINGLE_GROUP_REQUIRED') {
            toast('Solo puedes seleccionar un grupo por encuentro', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'GROUP_NOT_FOUND') {
            toast('El grupo seleccionado ya no está disponible', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENDS_AT_REQUIRED') {
            toast('Selecciona una hora estimada de finalización', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENDS_AT_INVALID') {
            toast('La hora estimada de finalización debe ser posterior a la hora de inicio', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'STARTS_AT_MUST_BE_FUTURE') {
            toast('No se pueden cargar eventos para hoy ni para fechas pasadas', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        toast('Error creando evento del calendario', {
            type: 'error'
        });
        return { success: false, code: errorCode };
    }
    return { success: false };
}

export async function updateCalendarEvent(eventId, payload) {
    try {
        const response = await httpInterceptor.put(`calendar-events/${eventId}`, payload);
        if (response?.status === 200 || response?.status === 201) {
            return {
                success: true,
                data: response?.data?.data ?? null
            };
        } else if (response && response.success) {
            return {
                success: true,
                data: response.data ?? null
            };
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();

        if (errorCode === 'EVENT_NOT_ALLOWED') {
            toast('No puedes editar este evento', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'STATUS_INVALID') {
            toast('Selecciona un estado valido', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'STARTS_AT_REQUIRED') {
            toast('Selecciona una fecha y hora valida', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'ENDS_AT_INVALID') {
            toast('La hora de finalizacion debe ser posterior al inicio', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        if (errorCode === 'STARTS_AT_MUST_BE_FUTURE') {
            toast('No se pueden programar eventos para hoy ni para fechas pasadas', {
                type: 'warning'
            });
            return { success: false, code: errorCode };
        }

        toast('Error actualizando evento del calendario', {
            type: 'error'
        });
        return { success: false, code: errorCode };
    }
    return { success: false };
}

export async function updateSchoolActive(schoolId, active) {
    try {
        const response = await httpInterceptor.put('schools/active', {schoolId, active});
        if (response?.status === 200) {
            return true;
        } else if (response && response.success) {
            return true;
        }
    } catch (error) {
        toast('Error actualizando estado del Proceso Formativo', {
            type: 'error'
        });
    }
    return false;
}

// Funciones para concertación

export async function saveExplorationEvidence(payload) {
    try {
        const resource = `meetings/exploration`;
        const formData = buildEvidenceFormData(payload, 5);
        const response = await httpInterceptor.post(resource, formData);

        if (response?.status === 200 || response?.success) return true;
        return false;
    } catch (error) {
        console.error(error?.response);
        if (error?.message === 'MAX_FILE_SIZE_10MB') {
            toast('Cada archivo debe pesar maximo 10MB', {
                type: 'warning'
            });
            return false;
        }

        toast('Error guardando evidencias de concertacion', {
        type: 'error'
        });

        return false;
    }
}

export async function updateExplorationEvidence(explorationId, payload) {
    try {
        const resource = `meetings/exploration/${explorationId}`;
        const formData = buildEvidenceFormData(payload, 5);
        const response = await httpInterceptor.put(resource, formData);

        if (response?.status === 200 || response?.success) return true;
        return false;
    } catch (error) {
        console.error(error?.response);
        if (error?.message === 'MAX_FILE_SIZE_10MB') {
            toast('Cada archivo debe pesar maximo 10MB', {
                type: 'warning'
            });
            return false;
        }

        toast('Error actualizando evidencias de concertacion', {
        type: 'error'
        });

        return false;
    }
}

export async function getExplorationsByFacilitator(userId) {
    try {
        const resource = `meetings/exploration-list?facilitator=${userId}`;
        const response = await httpInterceptor.get(resource);
        if (response?.status === 200) return response.data.data;
        return [];
    } catch (error) {
        toast('Error obteniendo evidencias de concertación', { type: 'error' });
        return [];
    }
}

export async function getExplorationBySchool(schoolId) {
    try {
        const resource = withNoCache(`meetings/exploration-by-school?schoolId=${schoolId}`);
        const res = await httpInterceptor.get(resource);

        // ✅ httpInterceptor a veces devuelve axiosResponse y a veces devuelve res.data directo
        const payload = res?.data ?? res;

        // payload puede ser:
        // - { success:true, data:{...} }
        // - { schoolId:..., meetingGroupId:... }
        const exp = payload?.data ?? payload;

        return exp ?? null;
    } catch (e) {
        console.error('getExplorationBySchool error:', e?.response?.status, e?.response?.data || e?.message);
        return null;
    }
}

// Fin funciones para concertación //

// Funciones para socialización

export async function saveSocializationEvidence(payload) {
    try {
        const resource = `meetings/socialization`;
        const formData = buildEvidenceFormData(payload, 5);
        const response = await httpInterceptor.post(resource, formData);

        if (response?.status === 200 || response?.success) return true;
        return false;
    } catch (error) {
        console.error(error?.response);
        if (error?.message === 'MAX_FILE_SIZE_10MB') {
            toast('Cada archivo debe pesar maximo 10MB', {
                type: 'warning'
            });
            return false;
        }

        toast('Error guardando evidencias de socializacion', {
        type: 'error'
        });

        return false;
    }
}

export async function updateSocializationEvidence(socializationId, payload) {
    try {
        const resource = `meetings/socialization/${socializationId}`;
        const formData = buildEvidenceFormData(payload, 5);
        const response = await httpInterceptor.put(resource, formData);

        if (response?.status === 200 || response?.success) return true;
        return false;
    } catch (error) {
        console.error(error?.response);
        if (error?.message === 'MAX_FILE_SIZE_10MB') {
            toast('Cada archivo debe pesar maximo 10MB', {
                type: 'warning'
            });
            return false;
        }

        toast('Error actualizando evidencias de socializacion', {
        type: 'error'
        });

        return false;
    }
}

export async function getSocializationsByFacilitator(userId) {
    try {
        const resource = `meetings/socialization-list?facilitator=${userId}`;
        const response = await httpInterceptor.get(resource);
        if (response?.status === 200) return response.data.data;
        return [];
    } catch (error) {
        toast('Error obteniendo evidencias de socialización', { type: 'error' });
        return [];
    }
}

export async function getSocializationBySchool(schoolId, socializationNumber = 1) {
    try {
        const resource = withNoCache(`meetings/socialization-by-school?schoolId=${schoolId}&socializationNumber=${socializationNumber}`);
        const res = await httpInterceptor.get(resource);

        // httpInterceptor a veces devuelve axiosResponse y a veces devuelve res.data directo
        const payload = res?.data ?? res;

        // payload puede ser:
        // - { success:true, data:{...} }
        // - { schoolId:..., meetingGroupId:... }
        const exp = payload?.data ?? payload;

        return exp ?? null;
    } catch (e) {
        console.error('getSocializationBySchool error:', e?.response?.status, e?.response?.data || e?.message);
        return null;
    }
}

// Fin funciones para socialización


export async function getBeneficiary(documentNumber) {
    try {
        if (documentNumber) {
            const resource = `beneficiary?document=${documentNumber}`;
            const response = await httpInterceptor.get(resource);
            let data = []
            if (response?.status === 200) {
                data = response.data.data;
            } else if (response && response.success) {
                data = response.data;
            }
            return data;
        }
    } catch (error) {
        toast('Error obteniendo beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function searchBeneficiariesByDocument(documentNumber, { groupId, schoolId, activityGroupId, documentType } = {}) {
    try {
        if (documentNumber) {
            const params = new URLSearchParams({
                document: String(documentNumber).trim()
            });

            if (groupId) params.set('groupId', groupId);
            if (schoolId) params.set('schoolId', schoolId);
            if (activityGroupId) params.set('activityGroupId', activityGroupId);
            if (documentType) params.set('documentType', documentType);

            const response = await httpInterceptor.get(`beneficiary/search?${params.toString()}`);
            let data = []
            if (response?.status === 200) {
                data = response.data.data;
            } else if (response && response.success) {
                data = response.data;
            }
            return Array.isArray(data) ? data : [];
        }
    } catch (error) {
        toast('Error buscando beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return [];
}

export async function getBeneficiaryById(id) {
    try {
        if (id) {
            const resource = `beneficiary?uid=${id}`;
            const response = await httpInterceptor.get(resource);
            let data = []
            if (response?.status === 200) {
                data = response.data.data;
            } else if (response && response.success) {
                data = response.data;
            }
            return data;
        }
    } catch (error) {
        toast('Error obteniendo beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function addBeneficiary(groupId, beneficiaryId, schoolId) {
    try {
        const resource = `beneficiary/group`;
        const response = await httpInterceptor.put(resource,
            {group: groupId, beneficiary: beneficiaryId, school: schoolId});
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }

    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        const errorData = error?.response?.data?.data;

        if (errorCode === 'BENEFICIARY_CONTEXT_DUPLICATE') {
            toast(
                errorData?.canReactivate
                    ? 'Este beneficiario ya existe inactivo en este grupo. Puedes reactivarlo.'
                    : 'Este beneficiario ya existe en este grupo',
                {
                    type: 'warning'
                }
            );
            return {
                blocked: true,
                canReactivate: Boolean(errorData?.canReactivate),
                beneficiary: errorData?.beneficiary ?? null
            };
        }

        toast('Error asociando beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}

export async function addBeneficiaryToActivityGroup(activityGroupId, beneficiaryId) {
    try {
        const response = await httpInterceptor.put('beneficiary/activity-group', {
            activityGroupId,
            beneficiary: beneficiaryId
        });
        return Boolean(response?.success || response?.status === 200);
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        const errorData = error?.response?.data?.data;
        if (errorCode === 'BENEFICIARY_CONTEXT_DUPLICATE') {
            toast(
                errorData?.canReactivate
                    ? 'Este beneficiario ya existe inactivo en este grupo. Puedes reactivarlo.'
                    : 'Este beneficiario ya existe en este grupo',
                {type: 'warning'}
            );
            return {
                blocked: true,
                canReactivate: Boolean(errorData?.canReactivate),
                beneficiary: errorData?.beneficiary ?? null
            };
        }
        toast('Error asociando beneficiario intente de nuevo', {type: 'error'});
    }
    return false;
}

export async function addBeneficiaryToSchool(schoolId, beneficiaryId) {
    try {
        const resource = `beneficiary/school`;
        const response = await httpInterceptor.put(resource,
            {school: schoolId, beneficiary: beneficiaryId});
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }

    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        const errorData = error?.response?.data?.data;

        if (errorCode === 'BENEFICIARY_CONTEXT_DUPLICATE') {
            toast(
                errorData?.canReactivate
                    ? 'Este beneficiario ya existe inactivo en esta asesoria. Puedes reactivarlo.'
                    : 'Este beneficiario ya existe en esta asesoria',
                {
                    type: 'warning'
                }
            );
            return {
                blocked: true,
                canReactivate: Boolean(errorData?.canReactivate),
                beneficiary: errorData?.beneficiary ?? null
            };
        }

        toast('Error asociando beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}

export async function duplicateBeneficiary(beneficiaryId, { schoolId, groupId, activityGroupId, userId } = {}) {
    try {
        const resource = `beneficiary/duplicate`;
        const response = await httpInterceptor.post(resource, {
            sourceBeneficiaryId: beneficiaryId,
            schoolId,
            groupId,
            activityGroupId,
            userId
        });

        if (response?.status === 200) {
            return response.data?.data ?? response.data ?? true;
        } else if (response && response.success) {
            return response.data ?? true;
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        const errorData = error?.response?.data?.data;

        if (errorCode === 'BENEFICIARY_CONTEXT_DUPLICATE') {
            toast(
                errorData?.canReactivate
                    ? 'Este beneficiario ya existe inactivo en este lugar. Puedes reactivarlo.'
                    : 'Este beneficiario ya existe en este lugar',
                {
                    type: 'warning'
                }
            );
            return {
                blocked: true,
                canReactivate: Boolean(errorData?.canReactivate),
                beneficiary: errorData?.beneficiary ?? null
            };
        }

        if (errorCode === 'NO_DUPLICATE_DOCUMENT_AVAILABLE') {
            toast('No se pudo generar un duplicado para este beneficiario', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'SCHOOL_NOT_FOUND' || errorCode === 'GROUP_NOT_FOUND') {
            toast('No se encontro el lugar donde se quiere duplicar', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_PARAM' || errorCode === 'SOURCE_BENEFICIARY_NOT_FOUND') {
            toast('No se pudo identificar el beneficiario para duplicar', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_SOURCE_DOCUMENT') {
            toast('El beneficiario no tiene un documento valido para duplicar', {
                type: 'warning'
            });
            return false;
        }

        toast('Error duplicando beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}

export async function reactivateBeneficiary(beneficiaryId) {
    try {
        const response = await httpInterceptor.put('beneficiary/reactivate', {
            beneficiaryId
        });

        if (response?.status === 200) {
            return response.data?.data ?? true;
        } else if (response && response.success) {
            return response.data ?? true;
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();

        if (errorCode === 'BENEFICIARY_CONTEXT_DUPLICATE') {
            toast('No se pudo reactivar porque ya existe otro registro en este lugar', {
                type: 'warning'
            });
            return false;
        }

        toast('Error reactivando beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}

export async function unbindBeneficiary(beneficiaryId, reason) {
    try {
        const resource = `beneficiary/unbind`;
        const response = await httpInterceptor.put(resource,
            {group: 0, beneficiaryId, school: 0, reason});
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }

    } catch (error) {
        toast('Error asociando beneficiario intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}

export async function getBeneficiaries(groupId, meetingId) {
    try {
        if (groupId || meetingId) {
            let resource = groupId ? `beneficiary/list-by-group?group=${groupId}` : 'beneficiary/list';
            if (meetingId) {
                resource = `beneficiary/list-by-meeting?meetingId=${meetingId}`;
            }
            const response = await httpInterceptor.get(resource);
            let data = []
            if (response?.status === 200) {
                data = response.data.data;
            } else if (response && response.success) {
                data = response.data;
            }
            return data;
        }
    } catch (error) {
        toast('Error obteniendo beneficiarios intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getBeneficiariesByActivityGroup(activityGroupId) {
    try {
        if (activityGroupId) {
            const response = await httpInterceptor.get(
                withNoCache(
                    `beneficiary/list-by-activity-group?activityGroupId=${activityGroupId}`
                )
            );
            if (response?.status === 200) {
                return response.data.data;
            }
            if (response?.success) {
                return response.data;
            }
        }
    } catch (error) {
        toast('Error obteniendo beneficiarios intente de nuevo', {
            type: 'error'
        });
    }
    return [];
}

export async function getBeneficiariesBySchool(schoolId) {
    try {
        if (schoolId) {
            const resource = withNoCache(`beneficiary/list-by-school?school=${schoolId}`);
            const response = await httpInterceptor.get(resource);
            let data = []
            if (response?.status === 200) {
                data = response.data.data;
            } else if (response && response.success) {
                data = response.data;
            }
            return data;
        }
    } catch (error) {
        toast('Error obteniendo beneficiarios intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getAllBeneficiaries(includeInactive = false, scope = '') {
    try {
        const params = new URLSearchParams();
        if (includeInactive) {
            params.set('includeInactive', 'true');
        }
        if (scope) {
            params.set('scope', scope);
        }
        const query = params.toString();
        const resource = query ? `beneficiary/list?${query}` : 'beneficiary/list';
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;

    } catch (error) {
        toast('Error obteniendo beneficiarios intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getList(type) {
    try {
        const resource = `lists/by-type?type=${type}`;
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;

    } catch (error) {
        toast('Error obteniendo lista intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getMeetings(year) {
    try {
        const resource = `meetings?year=${year}`;
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo encuentros intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getMeetingsByGroup(groupId) {
    try {
        const normalizedGroupId =
            typeof groupId === 'object' && groupId !== null
                ? groupId.id
                : groupId;
        const resource = `meetings/list-group?groupId=${normalizedGroupId}`;
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo lista de encuenrtros,  intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function updateMeetingEvidenceReview(meetingId, payload) {
    try {
        const response = await httpInterceptor.put(
            `meetings/${meetingId}/evidence-review`,
            payload
        );
        if (response?.status === 200) {
            return response.data.data;
        }
        if (response?.success) {
            return response.data;
        }
    } catch (error) {
        const message = error?.response?.data?.message;
        toast(
            message === 'REVIEW_OBSERVATIONS_REQUIRED'
                ? 'Escribe una observación para solicitar la subsanación.'
                : 'No fue posible guardar la revisión. Intenta nuevamente.',
            {type: 'error'}
        );
    }
    return null;
}

export async function updateIncidenceActionEvidenceReview(actionId, payload) {
    try {
        const response = await httpInterceptor.put(
            `incidence-actions/${actionId}/evidence-review`,
            payload
        );
        if (response?.status === 200) return response.data.data;
        if (response?.success) return response.data;
    } catch (error) {
        const message = error?.response?.data?.message;
        toast(
            message === 'REVIEW_OBSERVATIONS_REQUIRED'
                ? 'Escribe una observaciÃ³n para solicitar la subsanaciÃ³n.'
                : 'No fue posible guardar la revisiÃ³n. Intenta nuevamente.',
            {type: 'error'}
        );
    }
    return null;
}

export async function getEvidenceReviewTrafficLight(weekStart, endDate = '') {
    try {
        const endDateQuery = endDate
            ? `&endDate=${encodeURIComponent(endDate)}`
            : '';
        const response = await httpInterceptor.get(
            `meetings/evidence-review-traffic-light?weekStart=${encodeURIComponent(weekStart)}${endDateQuery}`
        );
        if (response?.status === 200) return response.data.data;
        if (response?.success) return response.data;
    } catch (error) {
        toast('No fue posible cargar el semáforo de subsanaciones.', {
            type: 'error'
        });
    }
    return null;
}

export async function createGeneralReviewMessage(payload) {
    try {
        const response = await httpInterceptor.post('meetings/general-review-messages', payload);
        if (response?.status === 201) return response.data.data;
        if (response?.success) return response.data;
    } catch (error) {
        toast('No fue posible enviar el mensaje. Intenta nuevamente.', {type: 'error'});
    }
    return null;
}

export async function closeGeneralReviewMessage(id) {
    try {
        const response = await httpInterceptor.put(`meetings/general-review-messages/${id}/close`, {});
        if (response?.status === 200) return response.data.data;
        if (response?.success) return response.data;
    } catch (error) {
        toast('No fue posible cerrar la solicitud. Intenta nuevamente.', {type: 'error'});
    }
    return null;
}

export async function getMeetingEvidenceHistory() {
    try {
        const response = await httpInterceptor.get('meetings/evidence-history');
        if (response?.status === 200) return response.data.data;
        if (response?.success) return response.data;
    } catch (error) {
        toast('No fue posible cargar el historial de evidencias.', {type: 'error'});
    }
    return [];
}

export async function getMeetingsByActivityGroup(activityGroupId) {
    try {
        const response = await httpInterceptor.get(
            `meetings/list-group?activityGroupId=${activityGroupId}`
        );
        if (response?.status === 200) {
            return response.data.data;
        }
        if (response?.success) {
            return response.data;
        }
    } catch (error) {
        toast('Error obteniendo lista de encuentros, intente de nuevo', {
            type: 'error'
        });
    }
    return [];
}

export async function addGroupSession(groupId) {
    if (!groupId) return null;

    try {
        const response = await httpInterceptor.post('meetings/group-session', {
            groupId
        });
        if (response?.status === 201 || response?.status === 200) {
            return response.data.data;
        } else if (response && response.success) {
            return response.data;
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        if (errorCode === 'GROUP_FINALIZADO') {
            toast('El grupo ya está finalizado', { type: 'warning' });
            return null;
        }
        if (errorCode === 'GROUP_CANCELADO') {
            toast('No se pueden agregar sesiones a un grupo cancelado', { type: 'warning' });
            return null;
        }
        if (errorCode === 'GROUP_NOT_FOUND') {
            toast('No encontramos el grupo', { type: 'warning' });
            return null;
        }

        toast('Error agregando la sesión', { type: 'error' });
    }
    return null;
}

export async function addControlSocialGroupSession(activityGroupId) {
    if (!activityGroupId) return null;

    try {
        const response = await httpInterceptor.post('meetings/group-session', {
            activityGroupId
        });
        if (response?.status === 201 || response?.status === 200) {
            return response.data.data;
        }
        if (response?.success) {
            return response.data;
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        if (errorCode === 'GROUP_FINALIZADO') {
            toast('El grupo ya est\u00e1 finalizado', {type: 'warning'});
        } else if (errorCode === 'GROUP_CANCELADO') {
            toast('No se pueden agregar sesiones a un grupo cancelado', {
                type: 'warning'
            });
        } else {
            toast('Error agregando la sesi\u00f3n', {type: 'error'});
        }
    }
    return null;
}

export async function getOrCreateAdvisorPhaseMeeting({ schoolId, phase, order, userId }) {
    try {
        const response = await httpInterceptor.post('meetings/advisor-phase', {
            schoolId,
            phase,
            order,
            userId
        });
        if (response?.status === 200) {
            return response.data.data;
        } else if (response && response.success) {
            return response.data;
        }
    } catch (error) {
        const messageCode = String(error?.response?.data?.message || '').trim();
        if (messageCode === 'ADVISOR_MEETING_ORDER_INVALID') {
            toast('Cada fase permite maximo 4 encuentros', {
                type: 'warning'
            });
            return null;
        }

        toast('Error preparando el encuentro de la asesoria', {
            type: 'error'
        });
    }
    return null;
}

export async function getMeetingsAttendance(meetingId) {
    try {
        const resource = `meetings/attendance-list?meetingId=${meetingId}`;
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo lista de encuentros,  intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getAllAttendances() {
    try {
        const resource = `meetings/attendance-all`;
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo lista de encuentros,  intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getAttendanceBeneficiaryCounts(scope = '') {
    try {
        const params = new URLSearchParams();
        if (scope) {
            params.set('scope', scope);
        }
        const query = params.toString();
        const resource = query
            ? `meetings/attendance-beneficiary-counts?${query}`
            : `meetings/attendance-beneficiary-counts`;
        const response = await httpInterceptor.get(resource);
        let data = {};
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo conteo de asistencias, intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function getAllMeetingEvidences() {
    try {
        const resource = `meetings/evidences-list`;
        const response = await httpInterceptor.get(resource);
        let data = [];
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo evidencias, intente de nuevo', {
            type: 'error'
        });
    }
    return [];
}

export async function downloadAllIncidenceActionsArchive() {
    return await httpInterceptor.get('meetings/incidence-actions-archive', {
        responseType: 'blob'
    });
}

export async function getAttendances(schoolId, groupId) {
    try {
        const resource = `meetings/attendance-list-group?schoolId=${schoolId}&groupId=${groupId}`;
        const response = await httpInterceptor.get(resource);
        let data = []
        if (response?.status === 200) {
            data = response.data.data;
        } else if (response && response.success) {
            data = response.data;
        }
        return data;
    } catch (error) {
        toast('Error obteniendo lista de encuentros,  intente de nuevo', {
            type: 'error'
        });
    }
    return null;
}

export async function saveMeetingGeneralInfo(meeting) {
    if (meeting) {
        try {
            const resource = `meetings`;
            const response = await httpInterceptor.post(resource, meeting);

            if (response?.status === 200) {
                return true
            } else if (response && response.success) {
                return true
            }

        } catch (error) {
            toast('Error guardando vigencia de encuentros intente de nuevo', {
                type: 'error'
            });
        }
        return false;
    }
}

export async function updateSchoolGroup(group) {
    if (group) {
        try {
            const resource = `schools/group`;
            const response = await httpInterceptor.put(resource, group);
            if (response?.status === 200) {
                return true
            } else if (response && response.success) {
                return true
            }
        } catch (error) {
            toast('Error actualizando grupo', {
                type: 'error'
            });
        }
        return false;
    }
}

export async function cancelSchoolGroup(groupId, schoolId) {
    if (!groupId || !schoolId) {
        return false;
    }

    try {
        const response = await httpInterceptor.put('schools/group/status', {
            groupId,
            schoolId,
            status: 'CANCELADO'
        });
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();

        if (errorCode === 'GROUP_NOT_FOUND') {
            toast('No encontramos el grupo para cancelar', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'GROUP_STATUS_INVALID') {
            toast('Estado de grupo invalido', {
                type: 'warning'
            });
            return false;
        }

        toast('Error cancelando grupo', {
            type: 'error'
        });
    }
    return false;
}

export async function finishSchoolGroup(groupId, schoolId, confirmations = {}) {
    if (!groupId || !schoolId) return false;

    try {
        const response = await httpInterceptor.put('schools/group/status', {
            groupId,
            schoolId,
            status: 'FINALIZADO',
            resultsLogConfirmed: confirmations.resultsLogConfirmed === true,
            finalizationConfirmed: confirmations.finalizationConfirmed === true
        });
        if (response?.status === 200) {
            return true;
        } else if (response && response.success) {
            return true;
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();
        if (errorCode === 'GROUP_NOT_FOUND') {
            toast('No encontramos el grupo para finalizar', { type: 'warning' });
            return false;
        }

        if (errorCode === 'GROUP_COMPLETED_MEETING_REQUIRED') {
            toast('Para finalizar el grupo debe existir al menos una sesión realizada', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'GROUP_RESULTS_LOG_REQUIRED') {
            toast('Debe guardar contenido en la bitácora de resultados antes de finalizar el grupo', { type: 'warning' });
            return false;
        }
        if (errorCode === 'GROUP_FINALIZATION_CONFIRMATIONS_REQUIRED') {
            toast('Debe marcar las dos confirmaciones antes de finalizar el grupo', { type: 'warning' });
            return false;
        }
        toast('Error finalizando el grupo', { type: 'error' });
    }
    return false;
}

export async function updateGroupProjectLink(groupId, description) {
    if (!groupId) {
        return false;
    }

    try {
        const resource = `schools/group/project-link`;
        const response = await httpInterceptor.put(resource, {
            groupId,
            description
        });

        if (response?.status === 200) {
            return response.data?.data ?? true;
        } else if (response && response.success) {
            return response.data ?? true;
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();

        if (errorCode === 'PROJECT_LINK_TOO_LONG') {
            toast('El link debe tener maximo 255 caracteres', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'PROJECT_LINK_INVALID') {
            toast('Ingresa un link valido del proyecto', {
                type: 'warning'
            });
            return false;
        }

        toast('Error guardando link del proyecto', {
            type: 'error'
        });
    }
    return false;
}

export async function createSchoolGroup(payload) {
    if (payload) {
        try {
            const resource = `schools/group`;
            const response = await httpInterceptor.post(resource, payload);
            if (response?.status === 200) {
                return {
                    success: true,
                    data: response?.data?.data ?? null
                };
            } else if (response && response.success) {
                return {
                    success: true,
                    data: response?.data ?? null
                };
            }
        } catch (error) {
            return {
                success: false,
                code: error?.response?.data?.message
            };
        }
    }
    return { success: false };
}

export async function deleteSchoolGroup(groupId, schoolId) {
    if (!groupId || !schoolId) {
        return false;
    }

    try {
        const resource = `schools/group`;
        const response = await httpInterceptor.delete(resource, {
            data: {
                groupId,
                schoolId
            }
        });
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }
    } catch (error) {
        console.error(error?.response || error);
        toast('Error eliminando grupo', {
            type: 'error'
        });
    }
    return false;
}

export async function deleteSchool(schoolId) {
    if (!schoolId) {
        return false;
    }

    try {
        const resource = `schools`;
        const response = await httpInterceptor.delete(resource, {
            data: {
                schoolId
            }
        });
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }
    } catch (error) {
        const errorCode = String(error?.response?.data?.message || '').trim();

        if (errorCode === 'SCHOOL_HAS_GROUPS') {
            toast('Para eliminar la entidad aliada primero debes borrar todos los grupos existentes.', {
                type: 'warning'
            });
            return false;
        }

        toast('Error eliminando entidad aliada', {
            type: 'error'
        });
    }
    return false;
}

export async function prepareAttendanceTemplate(meeting) {
    const response = await httpInterceptor.post('meetings/attendance-template', {
        meetingId: meeting.meetingId,
        meetingDate: meeting.meetingDate,
        startTime: meeting.startTime,
        endTime: meeting.endTime,
        topic: meeting.topic,
        goal: meeting.goal,
        hasCustomLocation: meeting.hasCustomLocation,
        place: meeting.place,
        address: meeting.address
    });
    return response?.status === 200 ? response.data.data : response?.data;
}

export async function checkMeetingAttendanceWindow(meetingId) {
    if (!meetingId) return false;
    try {
        const response = await httpInterceptor.get(withNoCache(`meetings/attendance-window?meetingId=${encodeURIComponent(meetingId)}`));
        if (response?.data?.closed) {
            toast(ATTENDANCE_CLOSED_MESSAGE, {type: 'warning'});
            return false;
        }
        return response?.data?.closed === false;
    } catch (error) {
        toast('No fue posible verificar el plazo de asistencia. Intente nuevamente.', {type: 'error'});
        return false;
    }
}

export async function saveMeetingAttendance(meeting) {
    if (meeting) {
        try {
            const resource = `meetings/attendance`;
            const formData = new FormData();

            appendIfDefined(formData, 'goal', meeting.goal);
            appendIfDefined(formData, 'topic', meeting.topic);
            formData.append('hasCustomLocation', String(meeting.hasCustomLocation === true));
            appendIfDefined(formData, 'place', meeting.place);
            appendIfDefined(formData, 'address', meeting.address);
            appendIfDefined(formData, 'meetingId', meeting.meetingId);
            appendIfDefined(formData, 'meetingNotes', meeting.meetingNotes);
            appendIfDefined(formData, 'meetingDate', meeting.meetingDate);
            appendIfDefined(formData, 'startTime', meeting.startTime);
            appendIfDefined(formData, 'endTime', meeting.endTime);
            appendIfDefined(formData, 'meetingType', meeting.meetingType);
            appendIfDefined(formData, 'edit', meeting.edit);
            formData.append('beneficiaries', JSON.stringify(meeting.beneficiaries || []));

            const filesSize = (meeting.images || []).reduce(
                (total, image) => total + Number(image?.file?.size || 0),
                0
            );
            if (filesSize > MAX_MEETING_REQUEST_FILES_SIZE_BYTES) {
                throw new Error('MAX_REQUEST_FILES_SIZE_5MB');
            }

            (meeting.images || []).forEach((image, index) => {
                const file = image?.file;
                if (!file) return;
                validateMaxSize(file);
                const slot = resolveImageSlot(image, index, 5);
                if (!slot) return;
                formData.append(`slot_${slot}`, file, file.name || `slot_${slot}`);
            });

            const response = await httpInterceptor.post(resource, formData);
            if (response?.status === 200) {
                return true
            } else if (response && response.success) {
                return true
            }

        } catch (error) {
            console.error(error?.response);
            const errorCode = String(error?.response?.data?.message || '').trim();
            if (errorCode === ATTENDANCE_WINDOW_CLOSED) {
                toast(ATTENDANCE_CLOSED_MESSAGE, {type: 'warning'});
                return false;
            }
            if (errorCode === 'INVALID_MEETING_TOPIC') {
                const maxLength = error?.response?.data?.maxLength ?? 255;
                toast(`El tema debe ser un texto de máximo ${maxLength} caracteres`, { type: 'warning' });
                return false;
            }
            if (errorCode === 'MEETING_LOCATION_REQUIRED') {
                toast('Ingrese el lugar y la dirección del encuentro.', {type: 'warning'});
                return false;
            }
            if (errorCode === 'MEETING_LOCATION_TOO_LONG') {
                toast('El lugar y la dirección permiten máximo 255 caracteres.', {type: 'warning'});
                return false;
            }
            if (errorCode === 'ATTENDANCE_TIME_REQUIRED') {
                toast('Ingrese la hora de inicio y finalizacion', {
                    type: 'warning'
                });
                return false;
            }
            if (errorCode === 'INVALID_TIME_RANGE') {
                toast('La hora de finalizacion debe ser posterior al inicio', {
                    type: 'warning'
                });
                return false;
            }
            if (errorCode === 'MEETING_DATE_MUST_BE_2026') {
                toast('Fecha no válida. Debe corresponder al año 2026', {
                    type: 'warning'
                });
                return false;
            }
            if (error?.message === 'MAX_REQUEST_FILES_SIZE_5MB' || errorCode === 'MAX_REQUEST_FILES_SIZE_5MB') {
                toast('Se excedió el tamaño máximo permitido: los archivos seleccionados no pueden superar 5 MB en total', {
                    type: 'warning'
                });
                return false;
            }
            if (error?.message === 'MAX_FILE_SIZE_10MB' || error?.response?.data?.message === 'MAX_FILE_SIZE_10MB') {
                toast('Cada archivo debe pesar maximo 10MB', {
                    type: 'warning'
                });
                return false;
            }
            if (errorCode === 'INVALID_IMAGE_DATA') {
                toast('No se pudo leer la imagen JPG. Verifica que el archivo no esté dañado e intenta seleccionarlo nuevamente.', {
                    type: 'error'
                });
                return false;
            }
            if (String(error?.response?.data?.message || '').indexOf('IMAGEN') !== -1) {
                toast(error?.response?.data?.message, {
                    type: 'error'
                });
            } else {
                toast('Error guardando asistencia', {
                    type: 'error'
                });
            }
        }
        return false;
    }
}

export async function saveBatchMeetingAttendance(payload) {
    try {
        const response = await httpInterceptor.post('meetings/attendance-batch', payload);
        if (response?.status === 200 || response?.success) {
            return response?.data ?? true;
        }
    } catch (error) {
        console.error(error?.response || error);
        const errorCode = String(error?.response?.data?.message || '').trim();
        if (errorCode === ATTENDANCE_WINDOW_CLOSED) {
            toast(ATTENDANCE_CLOSED_MESSAGE, {type: 'warning'});
            return false;
        }

        if (errorCode === 'SIGNATURE_REQUIRED') {
            toast('Debe firmar antes de guardar la asistencia', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'MEETINGS_REQUIRED') {
            toast('Selecciona al menos una sesion para firmar', {
                type: 'warning'
            });
            return false;
        }

        toast('Error guardando asistencia de la jornada', {
            type: 'error'
        });
    }
    return false;
}

export async function createPublicAttendanceQrToken(payload) {
    try {
        const response = await httpInterceptor.post('meetings/attendance-public-token', payload);
        if (response?.status === 200 || response?.success) {
            return response?.data ?? null;
        }
    } catch (error) {
        console.error(error?.response || error);
        const errorCode = error?.response?.data?.message || error?.message;
        if (errorCode === ATTENDANCE_WINDOW_CLOSED) {
            toast(ATTENDANCE_CLOSED_MESSAGE, {type: 'warning'});
            return null;
        }
        if (errorCode === 'SCHEDULE_OVERWRITE_CONFIRMATION') {
            return {
                requiresConfirmation: true,
                meetings: error?.response?.data?.data?.meetings || []
            };
        }
        if (errorCode === 'ATTENDANCE_TIME_REQUIRED') {
            toast('Ingresa la hora de inicio y finalizacion', {
                type: 'warning'
            });
            return null;
        }
        if (errorCode === 'INVALID_TIME_RANGE') {
            toast('La hora de finalizacion debe ser posterior al inicio', {
                type: 'warning'
            });
            return null;
        }
        toast('No se pudo generar el QR de asistencia', {
            type: 'error'
        });
    }
    return null;
}

export async function updateJourneySchedule(payload) {
    try {
        const response = await httpInterceptor.put('meetings/journey-schedule', payload);
        if (response?.status === 200 || response?.success) {
            return response?.data ?? null;
        }
    } catch (error) {
        console.error(error?.response || error);
        const errorCode = error?.response?.data?.message || error?.message;
        if (errorCode === 'SCHEDULE_OVERWRITE_CONFIRMATION') {
            return {
                requiresConfirmation: true,
                meetings: error?.response?.data?.data?.meetings || []
            };
        }
        if (errorCode === 'ATTENDANCE_TIME_REQUIRED') {
            toast('Ingresa la hora de inicio y finalizacion', { type: 'warning' });
            return null;
        }
        if (errorCode === 'INVALID_TIME_RANGE') {
            toast('La hora de finalizacion debe ser posterior al inicio', { type: 'warning' });
            return null;
        }
        toast('No se pudo guardar el horario de la jornada', { type: 'error' });
    }
    return null;
}

export async function getPublicAttendanceContext(token) {
    try {
        const response = await httpInterceptor.get(
            `public-attendance/context?token=${encodeURIComponent(token)}`
        );
        if (response?.status === 200 || response?.success) {
            return response?.data ?? null;
        }
    } catch (error) {
        if (error?.response?.data?.message === ATTENDANCE_WINDOW_CLOSED) {
            toast(ATTENDANCE_CLOSED_MESSAGE, {type: 'warning'});
            return {error: ATTENDANCE_WINDOW_CLOSED};
        }
        console.error(error?.response || error);
        toast('El enlace de registro no es valido o ya expiro', {
            type: 'error'
        });
    }
    return null;
}

export async function getPublicAttendanceNeighborhoods(token, municipality) {
    try {
        const response = await httpInterceptor.get(
            `public-attendance/neighborhoods?token=${encodeURIComponent(token)}&municipality=${encodeURIComponent(municipality)}`
        );
        if (response?.status === 200 || response?.success) {
            return response?.data ?? [];
        }
    } catch (error) {
        console.error(error?.response || error);
        toast('Error obteniendo barrios', {
            type: 'error'
        });
    }
    return [];
}

export async function registerPublicAttendance(payload) {
    try {
        const response = await httpInterceptor.post('public-attendance/register', payload);
        if (response?.status === 200 || response?.success) {
            return response?.data ?? true;
        }
    } catch (error) {
        console.error(error?.response || error);
        const errorCode = String(error?.response?.data?.message || '').trim();
        if (errorCode === ATTENDANCE_WINDOW_CLOSED) {
            toast(ATTENDANCE_CLOSED_MESSAGE, {type: 'warning'});
            return false;
        }

        if (errorCode === 'SIGNATURE_REQUIRED') {
            toast('Debe firmar antes de guardar la asistencia', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'BENEFICIARY_ALREADY_CHARACTERIZED') {
            toast('Este número de documento ya está caracterizado', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'MEETINGS_REQUIRED') {
            toast('Selecciona al menos una sesion para firmar', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_BENEFICIARY_DATA') {
            toast('Completa todos los campos obligatorios del formulario', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_EMAIL') {
            toast('Debes ingresar un email valido', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_PHONE') {
            toast('Debes ingresar un celular valido', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_BIRTHDATE') {
            toast('Debe ingresar una fecha valida', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'MINIMUM_AGE_REQUIRED') {
            toast('La persona debe tener al menos 6 años', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_ADDRESS') {
            toast('Por favor ingresa una direccion valida', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_DOCUMENT') {
            toast('El numero de documento solo debe contener digitos', {
                type: 'warning'
            });
            return false;
        }

        if (errorCode === 'INVALID_OR_EXPIRED_TOKEN') {
            toast('El enlace de registro no es valido o ya expiro', {
                type: 'error'
            });
            return false;
        }

        toast('Error guardando el registro de asistencia', {
            type: 'error'
        });
    }
    return false;
}

export async function lookupPublicMeetingAttendee(token, document) {
    try {
        const response = await httpInterceptor.post('public-attendance/lookup', {token, document});
        return response?.data ?? null;
    } catch (error) {
        const code = String(error?.response?.data?.message || '').trim();
        return {error: code || 'LOOKUP_FAILED'};
    }
}

export async function signPublicMeetingAttendance(payload) {
    try {
        const response = await httpInterceptor.post('public-attendance/sign', payload);
        return response?.data ?? true;
    } catch (error) {
        const code = String(error?.response?.data?.message || '').trim();
        return {error: code || 'SIGN_FAILED'};
    }
}

export async function getMeetingSatisfactionResponses(meetingId) {
    try {
        const response = await httpInterceptor.get(
            `meetings/satisfaction-responses?meetingId=${encodeURIComponent(meetingId)}`
        );
        return response?.data ?? null;
    } catch (error) {
        console.error(error?.response || error);
        toast('No fue posible consultar las respuestas de satisfacción', {type: 'error'});
        return null;
    }
}

export async function deleteAttendance(attendanceId) {
    try {
        const resource = `meetings/delete-attendance-record?attendanceId=${attendanceId}`;
        const response = await httpInterceptor.delete(resource)
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }

    } catch (error) {
        toast('Error elilminando asistencia intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}
export async function deleteImage(imageId) {
    try {
        const resource = `image?imageId=${imageId}`;
        const response = await httpInterceptor.delete(resource)
        if (response?.status === 200) {
            return true
        } else if (response && response.success) {
            return true
        }

    } catch (error) {
        toast('Error elilminando Imagen intente de nuevo', {
            type: 'error'
        });
    }
    return false;
}

